import { i18n } from '@lingui/core';
import { Trans } from '@lingui/macro';
import clsx from 'clsx';
import { forwardRef, useState } from 'react';
import IconMinus from 'icons/misc/minus.svg';
import IconPlus from 'icons/misc/plus.svg';
import styles from './ProductQuantitySelector.module.scss';

type QuantitySelectorLocation = 'product' | 'cart';

type ProductQuantitySelector = {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  location?: QuantitySelectorLocation;
  max?: number | null;
};

export const ProductQuantitySelector = forwardRef<
  HTMLInputElement | null,
  ProductQuantitySelector
>(({ value, onChange, className, location, max }, ref) => {
  return (
    <div
      className={clsx(styles.quantitySelector, className, {
        [styles['quantitySelector--product']]: location === 'product',
      })}
    >
      <button
        className={styles.quantitySelector__controls}
        aria-controls="qty-selector"
        aria-label={i18n._(
          /* i18n */ {
            id: 'quantitySelector.decrease',
            message: 'Decrease quantity',
          }
        )}
        tabIndex={-1}
        disabled={value < 2}
        type="button"
        onClick={() => {
          onChange(value - 1);
        }}
      >
        <IconMinus aria-hidden="true" />
      </button>

      <div>
        <input
          id="qty-selector"
          ref={ref}
          className={clsx(
            styles.quantitySelector__input,
            'text-utility-utility-mobile text-utility-utility-desktop'
          )}
          name="quantity"
          value={value}
          onChange={(event) => {
            onChange && onChange(+event.target.value || 1);
          }}
          aria-label={i18n._(
            /* i18n */ {
              id: 'quantitySelector.input',
              message: 'Quantity Selector',
            }
          )}
          aria-live="polite"
          aria-valuemin={1}
          aria-valuemax={100}
          aria-valuenow={1}
          type="number"
          role="spinbutton"
        />
        <label className="visually-hidden" htmlFor="qty-selector">
          <Trans id="product.quantity.selectorPlaceholder">Value</Trans>
        </label>
      </div>

      <button
        className={styles.quantitySelector__controls}
        aria-controls="qty-selector"
        aria-label={i18n._(
          /* i18n */ {
            id: 'quantitySelector.increase',
            message: 'Increase quantity',
          }
        )}
        tabIndex={-1}
        disabled={max ? value >= max : undefined}
        type="button"
        onClick={() => {
          onChange && onChange(value + 1);
        }}
      >
        <IconPlus aria-hidden="true" />
      </button>
    </div>
  );
});
ProductQuantitySelector.displayName = 'ProductQuantitySelector';

type ProductQuantitySelectorWithState = {
  value?: number;
  onChange?: (value: number) => void;
  className?: string;
  location?: QuantitySelectorLocation;
  max?: number | null;
};

export const ProductQuantitySelectorWithState = forwardRef<
  HTMLInputElement | null,
  ProductQuantitySelectorWithState
>(({ value, onChange, className, location, max }, ref) => {
  const [quantity, setQuantity] = useState(value ?? 1);

  const handleChange = (value: number) => {
    setQuantity(value);
    onChange && onChange(value);
  };

  return (
    <ProductQuantitySelector
      ref={ref}
      value={quantity}
      onChange={handleChange}
      className={className}
      location={location}
      max={max}
    />
  );
});

ProductQuantitySelectorWithState.displayName =
  'ProductQuantitySelectorWithState';
